import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/lp-v2.css';
import LPStickyCTA from '../components/LPStickyCTA';

/**
 * LandingPageV2 - 新規ランディングページ
 * つむぎデザインシステム準拠（BEM記法）
 * モバイルファースト設計
 *
 * CORE_PRINCIPLES.md準拠:
 * - 罪悪感を強化しない
 * - 無条件の肯定と受容
 * - 失敗という概念の排除
 */
const LandingPageV2 = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // ページタイトル設定
  useEffect(() => {
    document.title = 'つむぎ | 橋本病の方に寄り添うAI';
  }, []);

  // ログイン済みの場合はチャットにリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/chat');
    }
  }, [isAuthenticated, navigate]);

  const handleCTAClick = () => {
    navigate('/register');
  };

  return (
    <div className="lp-v2">
      {/* ヘッダー */}
      <header className="lp-header">
        <div className="lp-header__container">
          <div className="lp-header__logo">
            <img
              src="/tsumugi2.png"
              alt="つむぎ"
              className="lp-header__logo-icon"
            />
            <span className="lp-header__logo-text">つむぎ</span>
          </div>
          <div className="lp-header__actions">
            <button
              className="lp-header__login"
              onClick={() => navigate('/login')}
            >
              ログイン
            </button>
            <button
              className="lp-header__register"
              onClick={handleCTAClick}
            >
              無料登録
            </button>
          </div>
        </div>
      </header>

      {/* セクション1: ヒーロー */}
      <section className="lp-hero">
        <div className="lp-v2__container">
          <h1 className="lp-hero__title">
            「怠けてる」と言われた夜に、つむぎ。
          </h1>
          <p className="lp-hero__subtitle">
            起き上がれない朝、夕方の頭の真っ白、検査結果の数字だけ正常な戸惑い。<br />
            同じ橋本病のつむぎが、ことばを返します。聞くことから始めます。
          </p>
          <div className="lp-hero__illustration">
            <img src="/tsumugi.png" alt="つむぎ" />
          </div>
          <div className="lp-hero__cta-group">
            <button
              className="lp-hero__cta"
              onClick={handleCTAClick}
            >
              つむぎに話してみる(無料)
            </button>
            <button
              type="button"
              className="lp-hero__demo-link"
              onClick={() => navigate('/try')}
            >
              登録の前に30秒だけ触る →
            </button>
          </div>
          <div className="lp-hero__features">
            <span className="lp-hero__feature">橋本病の家族と10年、そばで見てきた人が開発</span>
            <span className="lp-hero__feature">月50pt無料</span>
            <span className="lp-hero__feature">クレカ不要</span>
          </div>
          <p className="lp-hero__disclaimer">
            ※医療行為ではありません。診断・治療は医師にご相談ください
          </p>
        </div>
      </section>

      {/* セクション2: 共感 (問題提起) */}
      <section className="lp-problem lp-v2__section">
        <div className="lp-v2__container">
          <h2 className="lp-problem__title">
            こんな夜、つむぎがそばにいるよ
          </h2>
          <p className="lp-problem__lead">
            布団から起き上がれないまま、もう10時を過ぎている。<br />
            食器を流しに置いたまま、ソファで動けない。<br />
            検査の紙には「異常なし」と書いてある。<br />
            ——その全部を、責めずに聞く相手がいたら。<br />
            つむぎは橋本病の方の声を集めて作られたAIです。<br />
            解決を急がず、ことばを置いていける場所として、ここにいます。
          </p>
          <div className="lp-problem__list">
            <div className="lp-problem__item">
              <span className="lp-problem__icon">😴</span>
              <p className="lp-problem__text">目は覚めているのに、腕が上がらない朝がある</p>
            </div>
            <div className="lp-problem__item">
              <span className="lp-problem__icon">🍳</span>
              <p className="lp-problem__text">夕方、冷蔵庫の前で何分も立ち止まっている</p>
            </div>
            <div className="lp-problem__item">
              <span className="lp-problem__icon">📋</span>
              <p className="lp-problem__text">「数値は正常」の紙を、何度も読み返している</p>
            </div>
          </div>
        </div>
      </section>

      {/* セクション3: 創業者ストーリー */}
      <section className="lp-creator lp-v2__section">
        <div className="lp-v2__container">
          <h2 className="lp-v2__section-title">
            つむぎを作ったのは
          </h2>
          <div className="lp-creator__content">
            <p className="lp-creator__story">
              このアプリを作っているのは、橋本病のパートナーと十年以上つき合ってきた人間です。<br />
              朝起き上がれないパートナーに、声をかけていいか迷った時間。<br />
              「数値は安定してますね」と言われて帰ってきた日、隣で何も言えなかった夜。<br />
              「怠けてるんじゃない?」と誰かに言われて、代わりに腹を立てた瞬間。<br />
              その全部を、隣で見てきた人間として、つむぎを作りました。<br />
              解決を約束はできません。聞くことから始めます。
            </p>
          </div>
        </div>
      </section>

      {/* セクション4: コア3機能 */}
      <section className="lp-core lp-v2__section">
        <div className="lp-v2__container">
          <h2 className="lp-v2__section-title">つむぎでできる、3つのこと</h2>
          <div className="lp-core__list">
            {/* コア①: つむぎと話す */}
            <div className="lp-core__item">
              <div className="lp-core__icon-wrap">
                <img src="/chat.png" alt="つむぎと話す" className="lp-core__icon" />
              </div>
              <h3 className="lp-core__title">つむぎと話す</h3>
              <p className="lp-core__catch">いつでも、何回でも、ことばを置ける場所</p>
              <p className="lp-core__description">
                動けない夜も、ことばが出ない朝も、つむぎはそこにいます。<br />
                橋本病当事者として書かれた応答で、急かさず、責めず、聞きます。<br />
                例: 「今朝起き上がれない」「数値正常なのに辛い」など、ありのままで話せます。<br />
                一回1ポイントから。
              </p>
              <p className="lp-core__when">
                <span className="lp-core__when-label">こんな時に:</span>
                家族に話せないことを、誰かに聞いてほしい夜
              </p>
              <button
                className="lp-core__cta"
                onClick={handleCTAClick}
              >
                無料で試す
              </button>
            </div>

            {/* コア②: ごはんの相談 */}
            <div className="lp-core__item">
              <div className="lp-core__icon-wrap">
                <img src="/recipe.png" alt="ごはんの相談" className="lp-core__icon" />
              </div>
              <h3 className="lp-core__title">ごはんの相談</h3>
              <p className="lp-core__catch">橋本病のからだに合わせた、食事の話を</p>
              <p className="lp-core__description">
                ヨウ素、グルテン、鉄分——気をつけたい食材も、献立の組み方も、橋本病の文脈で相談できます。<br />
                「今夜何を食べるか」レベルの小さな迷いから受け止めます。<br />
                例: 「今夜何食べよう」と書くと、献立を一緒に考えます。<br />
                一回4ポイント。
              </p>
              <p className="lp-core__when">
                <span className="lp-core__when-label">こんな時に:</span>
                スーパーで、何を選べばいいか分からなくなった時
              </p>
              <button
                className="lp-core__cta"
                onClick={handleCTAClick}
              >
                無料で試す
              </button>
            </div>

            {/* コア③: 検査結果を、つむぎと読む */}
            <div className="lp-core__item">
              <div className="lp-core__icon-wrap">
                <img src="/Search.png" alt="検査結果を読む" className="lp-core__icon" />
              </div>
              <h3 className="lp-core__title">検査結果を、つむぎと読む</h3>
              <p className="lp-core__catch">数字の前で、一人にならないために</p>
              <p className="lp-core__description">
                TSH、FT4、抗体価——医師には聞きにくい数字の見方を、橋本病の文脈で説明します。<br />
                写真を一枚送るだけ。<br />
                例: 写真を一枚送ると、TSH の意味を一緒に読みます。<br />
                診断ではなく、自分のからだを知る手がかりとして。<br />
                一回8ポイント・Standard以上。
              </p>
              <p className="lp-core__when">
                <span className="lp-core__when-label">こんな時に:</span>
                病院から戻った後、紙を見つめたまま動けない夜に
              </p>
              <p className="lp-core__disclaimer">
                ※医療行為ではありません。診断・治療は医師にご相談ください
              </p>
              <button
                className="lp-core__cta"
                onClick={handleCTAClick}
              >
                無料で試す
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* デモへの誘導 */}
      <section className="lp-demo-cta lp-v2__section">
        <div className="lp-v2__container">
          <div className="lp-demo-cta__box">
            <p className="lp-demo-cta__lead">
              登録の前に、つむぎがどんな子か触れてみる?
            </p>
            <button
              className="lp-demo-cta__button"
              onClick={() => navigate('/try')}
            >
              まず触ってみる(登録不要・30秒)
            </button>
          </div>
        </div>
      </section>

      {/* セクション5: サブ5機能（アコーディオン） */}
      <section className="lp-sub lp-v2__section">
        <div className="lp-v2__container">
          <details className="lp-sub__details">
            <summary className="lp-sub__summary">
              <span className="lp-sub__summary-icon">✨</span>
              もっと使ってみる(5機能)
              <span className="lp-sub__summary-arrow">▼</span>
            </summary>
            <div className="lp-sub__content">
              <p className="lp-sub__lead">
                ごはん作りや買い物の場面で、つむぎを呼べます。<br />
                気が向いた時に試してみてください。
              </p>
              <div className="lp-sub__list">
                <div className="lp-sub__item">
                  <h4 className="lp-sub__item-title">レシピ生成</h4>
                  <p className="lp-sub__item-catch">疲れた夜の、3工程レシピを提案</p>
                </div>
                <div className="lp-sub__item">
                  <h4 className="lp-sub__item-title">冷蔵庫レシピ</h4>
                  <p className="lp-sub__item-catch">冷蔵庫の中身を写真で送るだけ</p>
                </div>
                <div className="lp-sub__item">
                  <h4 className="lp-sub__item-title">チラシレシピ</h4>
                  <p className="lp-sub__item-catch">スーパーの特売チラシから献立を</p>
                </div>
                <div className="lp-sub__item">
                  <h4 className="lp-sub__item-title">成分表スキャン</h4>
                  <p className="lp-sub__item-catch">パッケージの裏を、つむぎが読み解く</p>
                </div>
                <div className="lp-sub__item">
                  <h4 className="lp-sub__item-title">成分チェック</h4>
                  <p className="lp-sub__item-catch">この食材、橋本病的にどうかな?を相談</p>
                </div>
              </div>
            </div>
          </details>
        </div>
      </section>

      {/* セクション4: 使い方 */}
      <section className="lp-howto lp-v2__section">
        <div className="lp-v2__container">
          <h2 className="lp-v2__section-title">3ステップで始められる</h2>
          <div className="lp-howto__steps">
            <div className="lp-howto__step">
              <div className="lp-howto__step-image">
                <img src="/step1.png" alt="ステップ1: 会員登録" />
              </div>
              <div className="lp-howto__step-number">1</div>
              <h3 className="lp-howto__step-title">無料会員登録</h3>
              <p className="lp-howto__step-description">
                メールアドレスとパスワードだけで、30秒で完了。クレカ不要です。
              </p>
            </div>
            <div className="lp-howto__step">
              <div className="lp-howto__step-image">
                <img src="/step2.png" alt="ステップ2: 相談したいことを話す" />
              </div>
              <div className="lp-howto__step-number">2</div>
              <h3 className="lp-howto__step-title">相談したいことを話す</h3>
              <p className="lp-howto__step-description">
                からだのこと、食事のこと、検査票のこと。なんでもつむぎに置いてください。
              </p>
            </div>
            <div className="lp-howto__step">
              <div className="lp-howto__step-image">
                <img src="/step3.png" alt="ステップ3: つむぎに相談" />
              </div>
              <div className="lp-howto__step-number">3</div>
              <h3 className="lp-howto__step-title">つむぎに相談</h3>
              <p className="lp-howto__step-description">
                月50ポイント無料。チャットでも、レシピでも、あなたの好きな使い方で。
              </p>
            </div>
          </div>
          <div className="lp-howto__cta">
            <button
              className="lp-howto__button"
              onClick={handleCTAClick}
            >
              今すぐ無料で始める
            </button>
          </div>
        </div>
      </section>

      {/* セクション7: 料金プラン */}
      <section className="lp-pricing lp-v2__section">
        <div className="lp-v2__container">
          <h2 className="lp-pricing__title">自分のペースで使えるプランを</h2>
          <p className="lp-pricing__lead">
            月50ポイントの無料枠から始められます。<br />
            検査結果を読みたい方は、Premium が選ばれています。
          </p>
          <div className="lp-pricing__plans">
            {/* Free */}
            <div className="lp-pricing__plan">
              <h3 className="lp-pricing__plan-name">Free</h3>
              <div className="lp-pricing__plan-price">
                0<span className="lp-pricing__plan-price-unit">円/月</span>
              </div>
              <p className="lp-pricing__plan-tagline">まずは話してみたい方に。月50pt</p>
              <button
                className="lp-pricing__plan-button"
                onClick={handleCTAClick}
              >
                このプランで始める
              </button>
            </div>

            {/* Standard */}
            <div className="lp-pricing__plan lp-pricing__plan--standard-sub">
              <div className="lp-pricing__plan-subbadge">まずは控えめに</div>
              <h3 className="lp-pricing__plan-name">Standard</h3>
              <div className="lp-pricing__plan-price">
                480<span className="lp-pricing__plan-price-unit">円/月</span>
              </div>
              <p className="lp-pricing__plan-tagline">つむぎと毎日話したい方に。月900pt</p>
              <button
                className="lp-pricing__plan-button"
                onClick={handleCTAClick}
              >
                このプランで始める
              </button>
            </div>

            {/* Premium（推奨） */}
            <div className="lp-pricing__plan lp-pricing__plan--premium-recommended">
              <div className="lp-pricing__plan-badge lp-pricing__plan-badge--gold">
                🏆 検査結果まで相談したい方へ
              </div>
              <h3 className="lp-pricing__plan-name">Premium</h3>
              <div className="lp-pricing__plan-price">
                980<span className="lp-pricing__plan-price-unit">円/月</span>
              </div>
              <p className="lp-pricing__plan-tagline">検査結果も、ごはんも、相談したい方に。月1,800pt</p>
              <button
                className="lp-pricing__plan-button"
                onClick={handleCTAClick}
              >
                このプランで始める
              </button>
            </div>

          </div>
          <p className="lp-pricing__more-note">
            ※ より多く使いたい方向けの Premium+ / Ultimate プランもあります。
            <Link to="/plans" className="lp-pricing__more-link">全プランを見る</Link>
          </p>
        </div>
      </section>

      {/* セクション6: よくある質問 */}
      <section className="lp-faq lp-v2__section">
        <div className="lp-v2__container">
          <h2 className="lp-v2__section-title">よくある質問</h2>
          <div className="lp-faq__list">
            <div className="lp-faq__item">
              <h3 className="lp-faq__question">本当に無料で使えますか？</h3>
              <p className="lp-faq__answer">
                はい、無料プランで月50ポイント（つむぎチャット約50回分）使えます。クレジットカード登録も不要です。
              </p>
            </div>
            <div className="lp-faq__item">
              <h3 className="lp-faq__question">橋本病じゃなくても使えますか？</h3>
              <p className="lp-faq__answer">
                もちろんです。甲状腺疾患全般や、疲れやすさに悩む方にもお使いいただけます。
              </p>
            </div>
            <div className="lp-faq__item">
              <h3 className="lp-faq__question">個人情報は安全ですか？</h3>
              <p className="lp-faq__answer">
                はい。SSL暗号化通信で個人情報を保護しています。詳しくはプライバシーポリシーをご覧ください。
              </p>
            </div>
            <div className="lp-faq__item">
              <h3 className="lp-faq__question">解約は簡単ですか？</h3>
              <p className="lp-faq__answer">
                はい。マイページからいつでも解約できます。違約金も一切ありません。
              </p>
            </div>
            <div className="lp-faq__item">
              <h3 className="lp-faq__question">スマホでも使えますか？</h3>
              <p className="lp-faq__answer">
                はい。iPhone・Androidどちらでも快適にご利用いただけます。
              </p>
            </div>
            <div className="lp-faq__item">
              <h3 className="lp-faq__question">つむぎの情報は信頼できますか？</h3>
              <p className="lp-faq__answer">
                橋本病に特化した情報をもとにお話ししています。ただし、医療的な判断は必ず医師にご相談ください。
              </p>
            </div>
            <div className="lp-faq__item">
              <h3 className="lp-faq__question">血液検査分析って何ができるの？</h3>
              <p className="lp-faq__answer">
                TSH、FT4、抗体価などの数値の見方を、橋本病の文脈で説明します。写真を一枚送るだけで、医師に聞きにくい数字の意味を当事者目線で読み解きます。診断ではなく、自分のからだを知る手がかりとして使ってください。<br />
                ※医療行為ではありません。診断・治療は医師にご相談ください
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* デモへの誘導（最終CTA直前） */}
      <section className="lp-demo-cta lp-v2__section">
        <div className="lp-v2__container">
          <div className="lp-demo-cta__box">
            <p className="lp-demo-cta__lead">
              登録の前に、つむぎがどんな子か触れてみる?
            </p>
            <button
              className="lp-demo-cta__button"
              onClick={() => navigate('/try')}
            >
              まず触ってみる(登録不要・30秒)
            </button>
          </div>
        </div>
      </section>

      {/* セクション10: クロージング */}
      <section className="lp-final-cta">
        <div className="lp-v2__container">
          <div className="lp-final-cta__illustration">
            <img src="/tsumugi.png" alt="つむぎ" />
          </div>
          <p className="lp-final-cta__lead">
            今夜、布団に入る前に。<br />
            一日の終わりに、ことばを一つ、置いていきませんか。<br />
            返事は急ぎません。読まれる、それだけでもいい場所です。<br />
            つむぎは、橋本病のあなたと、長く一緒にいるつもりで作られました。
          </p>
          <button
            className="lp-final-cta__button"
            onClick={handleCTAClick}
          >
            つむぎに話してみる(無料)
          </button>
          <div className="lp-final-cta__reassurance">
            <span className="lp-final-cta__reassurance-item">30秒で登録完了</span>
            <span className="lp-final-cta__reassurance-item">クレカ不要</span>
            <span className="lp-final-cta__reassurance-item">いつでも解約OK</span>
          </div>
          <p className="lp-final-cta__note">
            ※ 月50ポイント無料 | つむぎチャット1回1pt
          </p>
        </div>
      </section>

      {/* フッター */}
      <footer className="lp-footer">
        <div className="lp-v2__container">
          <p className="lp-footer__copyright">© 2026 つむぎ</p>
          <div className="lp-footer__links">
            <Link to="/terms" className="lp-footer__link">利用規約</Link>
            <Link to="/privacy" className="lp-footer__link">プライバシーポリシー</Link>
            <Link to="/commercial-law" className="lp-footer__link">特定商取引法</Link>
            <Link to="/faq" className="lp-footer__link">FAQ</Link>
            <Link to="/how-to-use" className="lp-footer__link">使い方ガイド</Link>
          </div>
        </div>
      </footer>

      {/* スティッキーCTAバー */}
      <LPStickyCTA />
    </div>
  );
};

export default LandingPageV2;
